import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Mail, Sparkles, Instagram, Twitter, Facebook, Linkedin, MessageSquare, ArrowRight, Loader2, CheckCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useNewsletterSubscribe } from "@/hooks/useNewsletterSubscribe";
import NewsletterHoneypot from "@/components/NewsletterHoneypot";
import { ADMIN_PATH } from "@/lib/admin-access";
import {
  shouldShowNewsletter,
  shouldShowUpgrade,
  shouldShowSocial,
  recordNewsletterShown,
  recordUpgradeShown,
  recordSocialShown,
  recordPageVisit,
} from "@/lib/engagement";

// Pages where popups should never appear
const BLOCKED_PATHS = ["/", "/auth", "/onboarding", "/reset-password", "/pricing", "/checkout/return", "/newsletter"];

type PopupType = "newsletter" | "upgrade" | "social" | null;

export default function EngagementPopups() {
  const { user, isPremium } = useAuth();
  const location = useLocation();
  const [activePopup, setActivePopup] = useState<PopupType>(null);
  const [dismissed, setDismissed] = useState(false);

  const isBlocked =
    BLOCKED_PATHS.includes(location.pathname) ||
    location.pathname.startsWith(ADMIN_PATH);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setActivePopup(null);
  }, []);

  useEffect(() => {
    if (!isBlocked) recordPageVisit();
  }, [location.pathname, isBlocked]);

  useEffect(() => {
    if (isBlocked || dismissed) return;
    const timer = setTimeout(() => {
      if (shouldShowNewsletter()) {
        setActivePopup("newsletter");
        recordNewsletterShown();
      } else if (user && !isPremium && shouldShowUpgrade()) {
        setActivePopup("upgrade");
        recordUpgradeShown();
      } else if (user && shouldShowSocial()) {
        setActivePopup("social");
        recordSocialShown();
      }
    }, 45000);
    return () => clearTimeout(timer);
  }, [isBlocked, dismissed, user, isPremium]);

  useEffect(() => {
    if (isBlocked || dismissed || activePopup) return;
    const earlyTimer = setTimeout(() => {
      if (shouldShowNewsletter()) {
        setActivePopup("newsletter");
        recordNewsletterShown();
      }
    }, 8000);
    return () => clearTimeout(earlyTimer);
  }, [location.pathname, isBlocked, dismissed, activePopup]);

  useEffect(() => {
    setDismissed(false);
    setActivePopup(null);
  }, [location.pathname]);

  return (
    <AnimatePresence>
      {activePopup === "newsletter" && <NewsletterPopup onClose={dismiss} />}
      {activePopup === "upgrade" && <UpgradePopup onClose={dismiss} />}
      {activePopup === "social" && <SocialPopup onClose={dismiss} />}
    </AnimatePresence>
  );
}

/* ─── Newsletter Popup ─────────────────────────────────── */
function NewsletterPopup({ onClose }: { onClose: () => void }) {
  const { email, setEmail, honeypot, setHoneypot, loading, done, handleSubmit } = useNewsletterSubscribe();

  useEffect(() => {
    if (done) {
      const t = setTimeout(onClose, 2000);
      return () => clearTimeout(t);
    }
  }, [done, onClose]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-background/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed z-[71] inset-x-0 bottom-0 sm:bottom-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-md sm:w-full"
      >
        <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl p-6 sm:p-8 shadow-2xl">
          <div className="w-12 h-1 rounded-full bg-muted mx-auto mb-4 sm:hidden" />
          <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors p-1" aria-label="Close"><X size={18} /></button>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4"><Mail className="text-accent" size={22} /></div>
            <h3 className="font-serif-display text-lg sm:text-xl font-bold text-foreground mb-2">The Council Speaks</h3>
            <p className="text-sm text-muted-foreground mb-6">Ancient frameworks for the decisions you're facing right now. Once a week, no fluff.</p>
            {done ? (
              <div className="flex items-center justify-center gap-2 text-accent py-4"><CheckCircle size={20} /><span className="font-medium">You're in. Watch your inbox.</span></div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <NewsletterHoneypot value={honeypot} onChange={setHoneypot} />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
                  className="w-full bg-secondary border border-border rounded-xl px-4 py-3 text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 min-h-[44px]" autoFocus />
                <button type="submit" disabled={loading}
                  className="w-full bg-accent text-accent-foreground py-3 rounded-xl font-semibold text-sm hover:bg-accent/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : "Join the Council"}
                </button>
              </form>
            )}
            {!done && (
              <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground mt-4 transition-colors min-h-[44px] px-4">No thanks</button>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}

/* ─── Upgrade Popup ────────────────────────────────────── */
function UpgradePopup({ onClose }: { onClose: () => void }) {
  const valueProps = [
    "Unlimited performance sessions",
    "All 4 wisdom personas unlocked",
    "Conversation memory & history",
    "Priority response speed",
  ];

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-background/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed z-[71] inset-x-4 bottom-4 sm:bottom-auto sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-md sm:w-full"
      >
        <div className="bg-card border border-accent/20 rounded-2xl p-6 sm:p-8 shadow-2xl relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-accent/5 rounded-full blur-3xl" />
          <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors p-1 z-10" aria-label="Close"><X size={18} /></button>
          <div className="relative z-10">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-4"><Sparkles className="text-accent" size={22} /></div>
            <h3 className="font-serif-display text-lg sm:text-xl font-bold text-foreground mb-2">Join the Inner Circle</h3>
            <p className="text-sm text-muted-foreground mb-5">You've been seeking wisdom — now unlock the full council.</p>
            <ul className="space-y-2.5 mb-6">
              {valueProps.map((prop) => (
                <li key={prop} className="flex items-start gap-2.5 text-sm text-foreground/90"><CheckCircle size={16} className="text-accent flex-shrink-0 mt-0.5" />{prop}</li>
              ))}
            </ul>
            <Link
              to="/pricing"
              onClick={onClose}
              className="w-full bg-accent text-accent-foreground py-3 rounded-xl font-semibold text-sm text-center hover:bg-accent/90 transition-colors min-h-[44px] flex items-center justify-center gap-2"
            >
              View Plans <ArrowRight size={16} />
            </Link>
            <button onClick={onClose} className="w-full text-xs text-muted-foreground hover:text-foreground mt-3 transition-colors min-h-[44px] px-4">Maybe later</button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

/* ─── Social / Feedback Popup ──────────────────────────── */
function SocialPopup({ onClose }: { onClose: () => void }) {
  const socials = [
    { icon: Instagram, label: "Instagram", href: "https://instagram.com/outspoken", color: "hover:text-pink-400" },
    { icon: Twitter, label: "Twitter", href: "https://twitter.com/outspoken", color: "hover:text-sky-400" },
    { icon: Facebook, label: "Facebook", href: "https://facebook.com/outspoken", color: "hover:text-blue-400" },
    { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com/company/outspoken", color: "hover:text-blue-500" },
  ];

  return (
    <motion.div
      initial={{ x: 100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 100, opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className="fixed z-[70] bottom-4 right-4 left-4 sm:left-auto sm:w-80"
    >
      <div className="bg-card border border-border rounded-2xl p-5 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center"><MessageSquare className="text-accent" size={16} /></div>
            <h4 className="font-serif-display text-sm text-foreground">Stay Connected</h4>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1" aria-label="Close"><X size={16} /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Follow us for daily wisdom, community stories, and new features.</p>
        <div className="flex items-center gap-3 mb-4">
          {socials.map(({ icon: Icon, label, href, color }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer"
              className={`w-10 h-10 rounded-xl bg-secondary flex items-center justify-center text-muted-foreground ${color} transition-colors`}
              aria-label={`Follow on ${label}`}>
              <Icon size={18} />
            </a>
          ))}
        </div>
        <Link
          to="/contact"
          onClick={onClose}
          className="w-full text-center bg-secondary hover:bg-muted text-foreground text-xs py-2.5 rounded-xl transition-colors min-h-[36px] flex items-center justify-center gap-1.5"
        >
          <MessageSquare size={14} />
          Share your feedback
        </Link>
      </div>
    </motion.div>
  );
}
